import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getUserById, updateUser, createClient, createUserUpdate, convertNestedEmptyToNull, useFreshClients } from '../services/firebase';
import { User, Client } from '../shared/types';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

//
// import Layout from '../components/Layout';
import LayoutOnboarding from '../components/LayoutOnboarding';
import { AppContainer } from '../components/AppContainer';
import ClientSelect from '../components/ClientSelect';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { Alert, AlertAction, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { FormSkeleton } from '@/components/FormSkeleton';
import { useSkeletonPhases } from '@/hooks/useSkeletonPhases';

type EditUserForm = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    campaignType?: User["campaignType"] | "";
    hideBooker?: boolean;
    clientObj?: Client;
};

/** Viewport-of-the-card, not the true field count. Extra `"input"`s fill the content area below chrome. */
const EDIT_USER_SKELETON_FIELDS = Array.from({ length: 10 }, () => "input" as const);

function EditUser() {
    const { uid } = useParams<{ uid: string }>();
    const [user, setUser] = useState<User>();
    const [newClient, setNewClient] = useState<Client>();
    const [formAlert, setFormAlert] = useState<string | null>(null);
    const {
        register,
        handleSubmit,
        setValue,
        control,
        formState: { errors },
    } = useForm<EditUserForm>();

    const queryClient = useQueryClient();
    const clientsQuery = useFreshClients();
    const clients = clientsQuery.flatData;
    const phase = useSkeletonPhases(!user);

    useEffect(() => {
        getInitialProps();

        async function getInitialProps() {
            console.log('getInitialProps!');

            if (uid) {
                const newUser = await getUserById(uid);
                setUser(newUser);

                if (newUser?.clientId) {
                    const client = clients.find((client) => client.id === newUser.clientId);
                    setValue('clientObj', client);
                }
            }
        }
    }, [uid, setValue, clients]);

    async function saveUser(formData: EditUserForm) {
        if (!uid) {
            return;
        }

        const { clientObj, ...owned } = formData;
        let clientId: string | undefined;
        if (newClient) {
            const res = await createClient({
                name: newClient.name,
                primaryBookerEmail: formData.email,
                primaryBookerId: uid,
            });

            if (res.success && typeof res.result === "string") {
                clientId = res.result;
            }
        } else if (typeof clientObj?.id === "string" && clientObj.id) {
            clientId = clientObj.id;
        }

        const res = await updateUser(createUserUpdate({
            id: uid,
            ...convertNestedEmptyToNull({
                ...owned,
                ...(clientId ? { clientId } : {}),
            }),
        }));

        // Invalidate all queries when the user is saved
        queryClient.invalidateQueries();

        if (res.success) {
            setFormAlert(null);
            toast.add({
                type: 'success',
                title: 'Changes saved',
                description: 'Your changes have been saved!',
            });
        } else {
            console.log(res.error);
            setFormAlert(`There was an issue saving your changes... ${JSON.stringify(res.error)}`);
        }

        console.log(res);
    }

    return (
        <LayoutOnboarding>
            <AppContainer className="mt-8 mb-8">
                {formAlert ? (
                    <Alert variant="destructive">
                        <AlertDescription>{formAlert}</AlertDescription>
                        <AlertAction>
                            <Button size="icon-xs" variant="ghost" onClick={() => setFormAlert(null)}>
                                <XIcon />
                                <span className="sr-only">Close</span>
                            </Button>
                        </AlertAction>
                    </Alert>
                ) : null}

                {phase === "showSkeleton" ? (
                    <div className="mx-auto w-100 max-w-full text-left">
                        <Card size="sm">
                            <CardContent>
                                <FormSkeleton fields={EDIT_USER_SKELETON_FIELDS} />
                            </CardContent>
                        </Card>
                    </div>
                ) : user ? (
                    <form className="mx-auto w-100 max-w-full text-left" onSubmit={handleSubmit(saveUser)}>
                        <Card size="sm">
                            <CardContent>
                                <FieldGroup>
                                    <div className="flex flex-wrap gap-4">
                                        <FormInput
                                            label="First Name"
                                            required
                                            fieldClassName="min-w-0 grow basis-auto"
                                            placeholder="First Name"
                                            error={errors.firstName ? 'First name is required.' : undefined}
                                            {...register('firstName', {
                                                required: true,
                                                value: user.firstName,
                                            })}
                                        />

                                        <FormInput
                                            label="Last Name"
                                            required
                                            fieldClassName="min-w-0 grow basis-auto"
                                            placeholder="Last Name"
                                            error={errors.lastName ? 'Last name is required.' : undefined}
                                            {...register('lastName', {
                                                required: true,
                                                value: user.lastName,
                                            })}
                                        />
                                    </div>

                                    <FormInput
                                        label="Email"
                                        required
                                        placeholder="Email"
                                        disabled
                                        error={errors.email ? 'Email is required.' : undefined}
                                        {...register('email', { required: true, value: user.email })}
                                    />

                                    <Field data-invalid={Boolean(errors.clientObj) || undefined}>
                                        <FieldLabel>
                                            Client <span className="text-destructive">*</span>
                                        </FieldLabel>

                                        <Controller
                                            control={control}
                                            name="clientObj"
                                            render={({ field: { onChange, value } }) => (
                                                <ClientSelect
                                                    disabled={Boolean(user?.clientId)}
                                                    value={value}
                                                    clients={clients}
                                                    onChange={(e, v) => {
                                                        if (typeof v === 'string') {
                                                            //
                                                        } else if (v?.val) {
                                                            onChange(v?.val);
                                                            // onChange(v);
                                                        } else if (v?.inputValue) {
                                                            setNewClient({ name: v.inputValue });
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Field>

                                    <Controller
                                        control={control}
                                        name="campaignType"
                                        rules={{ required: true }}
                                        defaultValue={user.campaignType || ''}
                                        render={({ field }) => (
                                            <FormSelect
                                                label="Campaign Type"
                                                required
                                                description="What type of promotions do they primarily run?"
                                                value={field.value || null}
                                                onChange={(value) => field.onChange(value ?? '')}
                                                placeholder="Choose Type..."
                                                error={errors.campaignType ? 'Campaign type is required.' : undefined}
                                            >
                                                <SelectItem value="Song">Song Promos</SelectItem>
                                                <SelectItem value="Brand">Brand Promos</SelectItem>
                                                <SelectItem value="Multi">Both (They work at an agency)</SelectItem>
                                            </FormSelect>
                                        )}
                                    />

                                    <Controller
                                        control={control}
                                        name="hideBooker"
                                        defaultValue={Boolean(user.hideBooker)}
                                        render={({ field }) => (
                                            <FormCheckbox
                                                label="Hide From Booker Selection?"
                                                description="Prevents booker from appearing when managers search for bookers when logging a new deal."
                                                checked={Boolean(field.value)}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />

                                    <FormInput
                                        label="Phone Number"
                                        placeholder="Phone"
                                        error={errors.phone ? 'Phone Number is required.' : undefined}
                                        {...register('phone', { required: false, value: user.phone })}
                                    />

                                    <Button type="submit">
                                        Save User
                                    </Button>
                                </FieldGroup>
                            </CardContent>
                        </Card>
                    </form>
                ) : null}
            </AppContainer>
        </LayoutOnboarding>
    );
}

export default EditUser;
